import React from 'react';

import { TeamNanoID } from '../../../../../../teams/teamsTypes';

import { ThreeDStockProductsListItemPreviewProduct } from './ThreeDStockProductsListItemPreview.types';
import {
  OnProductSimilarSearchAction,
  UpdateProductCacheAction
} from '../../../../../../products/productsTypes';
import { IconsEnum } from '../../../../../../../assets/icons/types';

import { useCurrentUser } from '../../../../../../../auth/hooks/useAuth';

import { ThreeDStockProductsListItemButtons } from '../ThreeDStockProductsListItemButtons';

import { Icon } from '../../../../../../../helpers/Icon';
import { PureIconButtonHelper } from '../../../../../../../helpers/buttons/PureIconButtonHelper';
import {
  ImageHelper,
  ImageItemImageVersions
} from '../../../../../../../helpers/ImageHelper';
import { CheckPermissions } from '../../../../../../../helpers/CheckPermissions';

import {
  ProductRenderTypeIcons,
  ProductsPermissions
} from '../../../../../../products/productsConstants';

import { Files } from '../../../../../../../utils/Files';
import { words } from '../../../../../../../locales/keys';

const srcSetVersions = {
  '1x': ImageItemImageVersions.MiniThumb320x320,
  '2x': ImageItemImageVersions.MiniThumb640x640,
  '3x': ImageItemImageVersions.MiniThumb960x960
};

interface ThreeDStockProductsListItemPreviewProps {
  downloadButtonAction: string;
  downloadSeparatelyButtonAction: string;
  findSimilarButtonAction: string;
  onEditButtonMouseEnter?: () => void;
  onLightboxOpen: () => void;
  onProductSimilarSearchFilter?: OnProductSimilarSearchAction;
  product: ThreeDStockProductsListItemPreviewProduct;
  productEditPagePath: string;
  updateProductCache?: UpdateProductCacheAction<ThreeDStockProductsListItemPreviewProduct>;
  companyNanoId?: TeamNanoID;
  editButtonAction?: string;
}

function ThreeDStockProductsListItemPreview({
  downloadButtonAction,
  downloadSeparatelyButtonAction,
  findSimilarButtonAction,
  onEditButtonMouseEnter,
  onLightboxOpen,
  onProductSimilarSearchFilter,
  product,
  productEditPagePath,
  updateProductCache,
  companyNanoId,
  editButtonAction
}: ThreeDStockProductsListItemPreviewProps) {
  const currentUser = useCurrentUser();

  return (
    <div className="aspect-w-1 aspect-h-1 w-full bg-gray-100 dark:bg-gray-800 order-first">
      {product.image?.file && Files.isImage(product.image?.file) ? (
        <ImageHelper
          className="object-cover w-full h-full"
          src={product.image.file}
          version={ImageItemImageVersions.MiniThumb320x320}
          srcSetVersions={srcSetVersions}
          alt={product.name}
        />
      ) : (
        <div className="flex items-center justify-center text-gray-600 absolute inset-0">
          <Icon icon={IconsEnum.PHOTOGRAPH_OUTLINE} className="h-12 w-12" />
        </div>
      )}

      <div className="absolute inset-0 md:bg-black md:bg-opacity-0 pointer-events-none md:group-hover:bg-opacity-40 group-focus-within:bg-opacity-40 group-hover:pointer-events-auto group-focus-within:pointer-events-auto transition-color duration-150">
        <CheckPermissions
          action={ProductsPermissions.READ_PRODUCT_RENDER_TYPES}
        >
          <div
            className={
              currentUser.hasPermissions(
                ProductsPermissions.READ_PRODUCT_RENDER_TYPES_ALWAYS_SHOW_ICON
              )
                ? 'absolute top-0 right-0 pt-3 pr-3'
                : 'absolute top-0 right-0 pt-3 pr-3 md:opacity-0 pointer-events-none md:group-hover:opacity-100 group-hover:pointer-events-auto group-focus-within:opacity-100 transition-opacity duration-150 ease-out'
            }
          >
            <div className="flex gap-1">
              {product.renderTypes?.map((type) => (
                <Icon
                  key={type.name}
                  icon={ProductRenderTypeIcons[type.name]}
                  className="rounded h-7 w-7"
                />
              ))}
            </div>
          </div>
        </CheckPermissions>
        <div className="absolute bottom-0 left-0 w-full pb-3 px-3 md:opacity-0 pointer-events-none md:group-hover:opacity-100 group-hover:pointer-events-auto md:group-focus-within:opacity-100 transition-opacity duration-150 ease-out z-[2]">
          <div>
            <ThreeDStockProductsListItemButtons
              downloadButtonAction={downloadButtonAction}
              downloadSeparatelyButtonAction={downloadSeparatelyButtonAction}
              editButtonAction={editButtonAction}
              findSimilarButtonAction={findSimilarButtonAction}
              onEditButtonMouseEnter={onEditButtonMouseEnter}
              onProductSimilarSearchFilter={onProductSimilarSearchFilter}
              product={product}
              productEditPagePath={productEditPagePath}
              updateProductCache={updateProductCache}
              companyNanoId={companyNanoId}
            />

            <PureIconButtonHelper
              className="bg-white text-black hover:bg-gray-100 p-2 w-full justify-center flex gap-2 items-center rounded-full mt-2 text-sm"
              iconClassName="h-5 w-5 stroke-2"
              icon={IconsEnum.ARROWS_EXPAND_OUTLINE}
              i18nTextClassName="block"
              i18nText={words.expand_view}
              onClick={onLightboxOpen}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default ThreeDStockProductsListItemPreview;
